// frontend/src/composables/useChart.js
import { ref, shallowRef, onMounted, onUnmounted, nextTick, watch, toRaw } from 'vue'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  // ДОБАВЛЯЕМ КОНТРОЛЛЕРЫ
  LineController,
  BarController,
  PieController,
  DoughnutController,
  RadarController,
  PolarAreaController,
  ScatterController,
  BubbleController
} from 'chart.js'

// Регистрируем ВСЕ необходимые компоненты Chart.js
ChartJS.register(
  // Шкалы
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,

  // Элементы
  PointElement,
  LineElement,
  BarElement,
  ArcElement,

  // Плагины (БЕЗ Filler - он вызывает проблемы)
  Title,
  Tooltip,
  Legend,

  // КОНТРОЛЛЕРЫ
  LineController,
  BarController,
  PieController,
  DoughnutController,
  RadarController,
  PolarAreaController,
  ScatterController,
  BubbleController
)

/**
 * Улучшенная композиция для работы с Chart.js
 * @param {Ref} canvasRef - ref элемента canvas
 * @param {Ref} chartConfig - конфигурация диаграммы
 * @param {Object} options - дополнительные опции
 */
export function useChart(canvasRef, chartConfig, options = {}) {
  // КРИТИЧЕСКИ ВАЖНО: Используем shallowRef для chart чтобы избежать глубокой реактивности
  const chart = shallowRef(null)
  const isLoading = ref(false)
  const error = ref(null)
  const isInitialized = ref(false)

  const {
    autoResize = true,
    responsive = true,
    maintainAspectRatio = false,
    onChartReady = null,
    onChartDestroyed = null,
    plugins = []
  } = options

  // Создание диаграммы
  const createChart = async () => {
    console.log('useChart.js: createChart вызван')
    console.log('canvasRef.value:', canvasRef.value)
    console.log('chartConfig.value:', chartConfig.value)
    
    // КРИТИЧЕСКАЯ ПРОВЕРКА: Canvas должен существовать и быть готовым
    if (!canvasRef.value) {
      console.warn('useChart.js: Canvas ref недоступен, попробуем позже')
      // Попробуем еще раз через небольшую задержку
      setTimeout(() => {
        if (canvasRef.value && chartConfig.value) {
          console.log('useChart.js: Повторная попытка создания диаграммы')
          createChart()
        }
      }, 100)
      return
    }
    
    if (!chartConfig.value) {
      console.warn('useChart.js: Chart config недоступен')
      return
    }

    // КРИТИЧЕСКАЯ ПРОВЕРКА: Canvas element должен быть полностью готов
    const canvas = canvasRef.value
    if (!canvas || !canvas.getContext) {
      console.warn('useChart.js: Canvas element не готов')
      return
    }

    // КРИТИЧЕСКАЯ ПРОВЕРКА: Получаем context и проверяем его
    let context
    try {
      context = canvas.getContext('2d')
      if (!context) {
        console.warn('useChart.js: Не удалось получить 2D context')
        return
      }
      console.log('useChart.js: Canvas context получен успешно')
    } catch (err) {
      console.error('useChart.js: Ошибка получения canvas context:', err)
      return
    }

    try {
      isLoading.value = true
      error.value = null

      // Уничтожаем существующую диаграмму если есть
      if (chart.value) {
        console.log('useChart.js: Уничтожаем существующую диаграмму')
        chart.value.destroy()
        chart.value = null
      }

      // ПОЛНАЯ ИЗОЛЯЦИЯ от Vue реактивности
      const safeConfig = {
        type: chartConfig.value.type,
        data: {
          labels: [...(chartConfig.value.data?.labels || [])],
          datasets: chartConfig.value.data?.datasets?.map(dataset => ({
            ...dataset,
            data: [...(dataset.data || [])]
          })) || []
        },
        options: {
          responsive,
          maintainAspectRatio,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                padding: 20,
                usePointStyle: true,
                font: { size: 12 }
              }
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderWidth: 1,
              cornerRadius: 8,
              padding: 12
            }
          },
          // Копируем дополнительные опции из chartConfig
          ...JSON.parse(JSON.stringify(chartConfig.value.options || {}))
        }
      }

      console.log('🔧 Создание диаграммы типа:', safeConfig.type)
      console.log('🔧 Canvas готов:', !!canvas)
      console.log('🔧 Context готов:', !!context)

      // Создаем новую диаграмму с дополнительными плагинами
      const config = {
        ...safeConfig,
        plugins: [...plugins]
      }

      // ВАЖНО: Передаем именно canvas element, а не ref
      chart.value = new ChartJS(canvas, config)
      isInitialized.value = true

      console.log('✅ Диаграмма создана успешно:', chart.value)

      // Вызываем колбэк готовности
      if (onChartReady) {
        try {
          onChartReady(chart.value)
        } catch (callbackErr) {
          console.error('❌ Ошибка в onChartReady:', callbackErr)
        }
      }

    } catch (err) {
      console.error('❌ Ошибка создания диаграммы:', err)
      error.value = err.message
      
      // Если произошла ошибка, очищаем chart
      if (chart.value) {
        try {
          chart.value.destroy()
        } catch (destroyErr) {
          console.warn('Ошибка при уничтожении диаграммы после ошибки:', destroyErr)
        }
        chart.value = null
      }
    } finally {
      isLoading.value = false
    }
  }

  // Обновление данных диаграммы
  const updateChart = (newData) => {
    if (!chart.value || !newData) {
      console.log('updateChart: chart или newData недоступны')
      return
    }

    try {
      console.log('updateChart: начинаем обновление диаграммы')
      
      // КРИТИЧЕСКИ ВАЖНО: Полная изоляция данных от Vue реактивности
      const safeData = {
        labels: newData.labels ? newData.labels.map(label => 
          typeof label === 'string' ? label : String(label)
        ) : [],
        datasets: newData.datasets ? newData.datasets.map(dataset => ({
          label: typeof dataset.label === 'string' ? dataset.label : String(dataset.label),
          data: dataset.data ? dataset.data.map(value => 
            typeof value === 'number' ? value : Number(value) || 0
          ) : [],
          backgroundColor: dataset.backgroundColor ? [...dataset.backgroundColor] : [],
          borderColor: dataset.borderColor,
          borderWidth: dataset.borderWidth,
          hoverBorderWidth: dataset.hoverBorderWidth,
          hoverOffset: dataset.hoverOffset
        })) : []
      }

      console.log('updateChart: безопасные данные подготовлены')

      // Обновляем данные в диаграмме
      if (safeData.labels && safeData.labels.length > 0) {
        chart.value.data.labels = safeData.labels
        console.log('updateChart: labels обновлены, количество:', safeData.labels.length)
      }

      if (safeData.datasets && safeData.datasets.length > 0) {
        chart.value.data.datasets = safeData.datasets
        console.log('updateChart: datasets обновлены, количество:', safeData.datasets.length)
      }

      // Обновляем диаграмму
      chart.value.update('active')
      console.log('✅ updateChart: Диаграмма обновлена успешно')
      
    } catch (err) {
      console.error('❌ updateChart: Ошибка обновления диаграммы:', err)
      error.value = err.message
      
      // В случае ошибки попробуем пересоздать диаграмму
      console.log('updateChart: Попытка пересоздания диаграммы из-за ошибки')
      setTimeout(() => {
        if (chartConfig.value) {
          createChart()
        }
      }, 100)
    }
  }

  // Обновление конфигурации диаграммы
  const updateConfig = (newConfig) => {
    if (!chart.value || !newConfig) return

    try {
      // ВАЖНО: Используем toRaw для избежания бесконечной рекурсии
      const rawConfig = toRaw(newConfig)

      // Обновляем опции
      Object.assign(chart.value.options, rawConfig.options || {})

      // Обновляем данные если переданы
      if (rawConfig.data) {
        updateChart(rawConfig.data)
      } else {
        chart.value.update('none')
      }

      console.log('✅ Конфигурация диаграммы обновлена')
    } catch (err) {
      console.error('❌ Ошибка обновления конфигурации:', err)
      error.value = err.message
    }
  }

  // Анимация диаграммы
  const animateChart = (duration = 1000) => {
    if (!chart.value) return

    chart.value.options.animation = {
      duration,
      easing: 'easeOutQuart'
    }
    chart.value.update('show')
  }

  // Экспорт диаграммы в изображение
  const exportChart = (format = 'png', quality = 0.8) => {
    if (!chart.value) return null

    if (format === 'png') {
      return chart.value.toBase64Image()
    } else if (format === 'jpg') {
      return chart.value.toBase64Image('image/jpeg', quality)
    }
  }

  // Добавление плагина
  const addPlugin = (plugin) => {
    if (chart.value && plugin) {
      chart.value.options.plugins = chart.value.options.plugins || {}
      chart.value.options.plugins[plugin.id] = plugin
      chart.value.update()
    }
  }

  // Удаление плагина
  const removePlugin = (pluginId) => {
    if (chart.value && chart.value.options.plugins && chart.value.options.plugins[pluginId]) {
      delete chart.value.options.plugins[pluginId]
      chart.value.update()
    }
  }

  // Уничтожение диаграммы
  const destroyChart = () => {
    if (chart.value) {
      chart.value.destroy()
      chart.value = null
      isInitialized.value = false

      if (onChartDestroyed) {
        onChartDestroyed()
      }

      console.log('🗑️ Диаграмма уничтожена')
    }
  }

  // Изменение размера
  const resizeChart = () => {
    if (chart.value) {
      chart.value.resize()
    }
  }

  // Получение данных точки при клике
  const getElementsAtEvent = (event) => {
    if (!chart.value) return []
    return chart.value.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true)
  }

  // Автоматическое изменение размера при изменении размера окна
  if (autoResize) {
    const handleResize = () => {
      setTimeout(resizeChart, 100)
    }

    onMounted(() => {
      window.addEventListener('resize', handleResize)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
    })
  }

  // ИСПРАВЛЕННЫЙ WATCHER - убираем рекурсию
  watch(chartConfig, (newConfig, oldConfig) => {
    console.log('useChart.js: watcher chartConfig сработал')
    console.log('newConfig:', !!newConfig)
    console.log('oldConfig:', !!oldConfig)
    
    // Избегаем создания диаграммы если конфиг не изменился существенно
    if (!newConfig) {
      console.log('useChart.js: newConfig пустой, пропускаем')
      return
    }
    
    // Проверяем, действительно ли конфиг изменился
    if (oldConfig && JSON.stringify(newConfig) === JSON.stringify(oldConfig)) {
      console.log('useChart.js: конфиг не изменился, пропускаем пересоздание')
      return
    }
    
    if (canvasRef.value) {
      console.log('useChart.js: пересоздаем диаграмму из-за изменения конфига')
      createChart()
    } else {
      console.log('useChart.js: canvas не готов, ждем nextTick')
      nextTick(() => {
        if (canvasRef.value && chartConfig.value) {
          createChart()
        }
      })
    }
  }, { 
    deep: false,  // НЕ используем deep watching - это вызывает рекурсию
    flush: 'post' // Выполняем после обновления DOM
  })

  onMounted(async () => {
    console.log('useChart.js: onMounted вызван')
    
    // Ждем следующий тик чтобы убедиться что DOM готов
    await nextTick()
    
    console.log('useChart.js: после nextTick, canvasRef:', !!canvasRef.value)
    console.log('useChart.js: после nextTick, chartConfig:', !!chartConfig.value)
    
    // Проверяем готовность элементов
    if (chartConfig.value && canvasRef.value) {
      console.log('useChart.js: создаем диаграмму в onMounted')
      createChart()
    } else {
      console.log('useChart.js: элементы не готовы в onMounted, ждем watcher')
    }
  })

  onUnmounted(() => {
    destroyChart()
  })

  return {
    chart,
    isLoading,
    error,
    isInitialized,
    createChart,
    updateChart,
    updateConfig,
    animateChart,
    exportChart,
    addPlugin,
    removePlugin,
    destroyChart,
    resizeChart,
    getElementsAtEvent,
    // Добавляем метод для ручного обновления
    refreshChart: () => {
      if (chartConfig.value) {
        createChart()
      }
    }
  }
}

/**
 * Расширенные генераторы цветов для диаграмм
 */
export const chartColors = {
  primary: [
    '#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#DBEAFE'
  ],
  success: [
    '#22C55E', '#4ADE80', '#86EFAC', '#BBF7D0', '#DCFCE7'
  ],
  warning: [
    '#F59E0B', '#FBBF24', '#FCD34D', '#FDE68A', '#FEF3C7'
  ],
  danger: [
    '#EF4444', '#F87171', '#FCA5A5', '#FECACA', '#FEE2E2'
  ],
  purple: [
    '#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE', '#EDE9FE'
  ],
  pink: [
    '#EC4899', '#F472B6', '#F9A8D4', '#FBCFE8', '#FCE7F3'
  ],
  cyan: [
    '#06B6D4', '#22D3EE', '#67E8F9', '#A5F3FC', '#CFFAFE'
  ],
  indigo: [
    '#6366F1', '#818CF8', '#A5B4FC', '#C7D2FE', '#E0E7FF'
  ],
  mixed: [
    '#3B82F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
  ],
  gradients: {
    blue: ['#3B82F6', '#1E40AF'],
    green: ['#22C55E', '#15803D'],
    orange: ['#F59E0B', '#D97706'],
    red: ['#EF4444', '#DC2626'],
    purple: ['#8B5CF6', '#7C3AED'],
    pink: ['#EC4899', '#BE185D'],
    cyan: ['#06B6D4', '#0891B2']
  }
}

/**
 * Создает конфигурацию для круговой диаграммы с валидацией данных
 */
export function createPieChartConfig(data, labelField = '_id', valueField = 'count', options = {}) {
  // ВАЛИДАЦИЯ ДАННЫХ
  if (!data) {
    console.warn('createPieChartConfig: data is null or undefined')
    return null
  }

  if (!Array.isArray(data)) {
    console.warn('createPieChartConfig: data is not an array, received:', typeof data, data)
    return null
  }

  if (data.length === 0) {
    console.warn('createPieChartConfig: data array is empty')
    return null
  }

  const {
    showLegend = true,
    legendPosition = 'right',
    showPercentages = true,
    colors = chartColors.mixed,
    responsive = true,
    cutout = 0
  } = options

  try {
    // Безопасное извлечение данных с проверками
    const labels = data.map(item => {
      if (!item || typeof item !== 'object') {
        return 'Неизвестно'
      }
      return item[labelField] || 'Не указано'
    })

    const values = data.map(item => {
      if (!item || typeof item !== 'object') {
        return 0
      }
      const value = item[valueField]
      return (typeof value === 'number' && !isNaN(value)) ? value : 0
    })

    const total = values.reduce((sum, val) => sum + val, 0)

    // Проверяем, что у нас есть данные для отображения
    if (total === 0) {
      console.warn('createPieChartConfig: all values are zero')
      return null
    }

    return {
      type: cutout > 0 ? 'doughnut' : 'pie',
      data: {
        labels,
        datasets: [{
          data: values,
          backgroundColor: colors.slice(0, data.length),
          borderWidth: 2,
          borderColor: '#ffffff',
          hoverBorderWidth: 3,
          hoverOffset: 4
        }]
      },
      options: {
        responsive,
        maintainAspectRatio: false,
        cutout: cutout > 0 ? `${cutout}%` : 0,
        plugins: {
          legend: {
            display: showLegend,
            position: legendPosition,
            labels: {
              generateLabels: function(chart) {
                const chartData = chart.data
                if (chartData.labels.length && chartData.datasets.length) {
                  return chartData.labels.map((label, i) => {
                    const value = chartData.datasets[0].data[i]
                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0

                    return {
                      text: showPercentages ?
                        `${label}: ${value.toLocaleString('ru-RU')} (${percentage}%)` :
                        `${label}: ${value.toLocaleString('ru-RU')}`,
                      fillStyle: chartData.datasets[0].backgroundColor[i],
                      strokeStyle: chartData.datasets[0].borderColor,
                      lineWidth: chartData.datasets[0].borderWidth,
                      hidden: false,
                      index: i
                    }
                  })
                }
                return []
              },
              padding: 15,
              usePointStyle: true,
              font: {
                size: 12
              }
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const label = context.label || ''
                const value = context.parsed || 0
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0
                return `${label}: ${value.toLocaleString('ru-RU')} (${percentage}%)`
              }
            }
          }
        },
        animation: {
          animateRotate: true,
          animateScale: false,
          duration: 1000
        },
        interaction: {
          intersect: false
        }
      }
    }

  } catch (error) {
    console.error('createPieChartConfig: Error creating config:', error)
    return null
  }
}

/**
 * Создает конфигурацию для линейной диаграммы
 */
export function createLineChartConfig(data, datasets = [], options = {}) {
  const {
    colors = chartColors.primary,
    smooth = true,
    tension = 0.4,
    pointRadius = 3,
    responsive = true,
    showGrid = true
  } = options

  if (!data || data.length === 0) {
    return null
  }

  const labels = data.map(item => {
    if (item.date) {
      const date = new Date(item.date)
      return date.toLocaleDateString('ru-RU', {
        month: 'short',
        day: 'numeric'
      })
    }
    return item.label || item._id || 'Неизвестно'
  })

  const chartDatasets = datasets.length > 0 ? datasets : [
    {
      label: 'Значения',
      data: data.map(item => item.value || item.count || 0),
      borderColor: colors[0],
      backgroundColor: colors[0],
      borderWidth: 2,
      tension: smooth ? tension : 0,
      pointRadius,
      pointHoverRadius: pointRadius + 2,
      pointBackgroundColor: colors[0],
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
      pointHoverBackgroundColor: colors[0],
      pointHoverBorderColor: '#ffffff',
      pointHoverBorderWidth: 3
    }
  ]

  return {
    type: 'line',
    data: {
      labels,
      datasets: chartDatasets
    },
    options: {
      responsive,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index'
      },
      plugins: {
        tooltip: {
          callbacks: {
            title: function(context) {
              return context[0].label
            },
            label: function(context) {
              return `${context.dataset.label}: ${context.parsed.y.toLocaleString('ru-RU')}`
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            display: showGrid,
            color: 'rgba(0, 0, 0, 0.1)'
          },
          ticks: {
            callback: function(value) {
              return value.toLocaleString('ru-RU')
            }
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      },
      animation: {
        duration: 1000,
        easing: 'easeOutQuart'
      }
    }
  }
}

/**
 * Валидация данных диаграммы - улучшенная версия
 */
export const validateChartData = (data, requiredFields = []) => {
  if (!data) {
    return {
      isValid: false,
      error: 'Данные не переданы',
      data: null
    }
  }

  if (!Array.isArray(data)) {
    return {
      isValid: false,
      error: `Данные должны быть массивом, получен: ${typeof data}`,
      data: null
    }
  }

  if (data.length === 0) {
    return {
      isValid: false,
      error: 'Массив данных пустой',
      data: []
    }
  }

  // Проверяем обязательные поля если указаны
  if (requiredFields.length > 0) {
    const invalidItems = data.filter(item => {
      if (!item || typeof item !== 'object') return true

      return requiredFields.some(field => {
        return item[field] === null || item[field] === undefined
      })
    })

    if (invalidItems.length > 0) {
      return {
        isValid: false,
        error: `Найдены элементы без обязательных полей: ${requiredFields.join(', ')}`,
        data: data.filter(item => !invalidItems.includes(item))
      }
    }
  }

  return {
    isValid: true,
    error: null,
    data: data
  }
}

/**
 * Форматирование данных для диаграмм
 */
export const formatChartData = (data, labelField = '_id', valueField = 'count') => {
  if (!data || !Array.isArray(data)) {
    return { labels: [], values: [] }
  }

  const labels = data.map(item => {
    const label = item[labelField]
    if (typeof label === 'string' && label.length > 30) {
      return label.substring(0, 30) + '...'
    }
    return label || 'Не указано'
  })

  const values = data.map(item => {
    const value = item[valueField]
    return typeof value === 'number' ? value : 0
  })

  return { labels, values }
}

/**
 * Утилиты для форматирования данных
 */
export const chartUtils = {
  // Форматирование числа
  formatNumber: (value) => {
    if (value === null || value === undefined) return '0'
    return Number(value).toLocaleString('ru-RU')
  },

  // Форматирование денег
  formatCurrency: (value) => {
    if (value === null || value === undefined) return '0 ₽'
    return Number(value).toLocaleString('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })
  },

  // Форматирование процентов
  formatPercentage: (value, total) => {
    if (!total || total === 0) return '0%'
    const percentage = (value / total) * 100
    return `${percentage.toFixed(1)}%`
  }
}

export default {
  useChart,
  chartColors,
  chartUtils,
  createPieChartConfig,
  createLineChartConfig,
  formatChartData,
  validateChartData
}